import React, { useContext } from "react";
import { useQuery } from "@apollo/client/react/hooks/useQuery.js";
import { Content } from "../layout/CommonContentContextProvider.js";
import {
    GET_NEWS_FEED,
    GetNewsFeedVariables,
    GetNewsFeedData,
} from "../../graphql/queries/news.js";
import Spinner from "../Spinner.js";
import MetaTags from "../MetaTags.js";
import ErrorMsg from "../common/errors/ErrorMsg.js";
import {
    gaFeedItemClick,
    gaLoadMoreData,
} from "../../helpers/googleAnalytics.js";
import LoadMoreButton from "../common/LoadMoreButton.js";
import AppBanners from "../common/banners/AppBanners.js";
import Card from "../common/Card.js";
import AppBannersContainer from "../common/banners/AppBannersContainer.js";
import { FeedContainer } from "../common/FeedContainers.js";
import { ConfigContext } from "../AppConfigProvider.js";
import {
    appBannerContainer,
    commonDescription,
    commonGridedTitle,
    container,
} from "../styles/common.css.js";
import NotFoundErrorMsg from "../common/errors/NotFoundErrorMsg.js";

const NewsFeed: React.FC = () => {
    const {
        langCode,
        langCodes,
        newsFeedPage: {
            title,
            metaTitle,
            metaDescription,
            description,
            keywords,
        },
        newsFeedMediaItems,
    } = useContext(Content);
    const { newsFeedPageItemsCount, promoApps, appleAppMarketId } =
        useContext(ConfigContext);

    const { loading, error, data, fetchMore } = useQuery<
        GetNewsFeedData,
        GetNewsFeedVariables
    >(GET_NEWS_FEED, {
        variables: {
            langCode,
            first: newsFeedPageItemsCount,
            after: undefined,
        },
        notifyOnNetworkStatusChange: true, // TODO: what is it?
    });

    if (error) {
        return <ErrorMsg error={error} />;
    }

    if (data && data.newsFeed) {
        const { newsFeed } = data;
        const handleLoadMore = () => {
            gaLoadMoreData(langCode, "/news");
            fetchMore({
                variables: {
                    langCode,
                    first: newsFeedPageItemsCount,
                    after: newsFeed.edges[newsFeed.edges.length - 1].cursor,
                },
            });
        };

        const { totalCount, offset, edges } = newsFeed;

        const hasMoreNews = totalCount - offset >= newsFeedPageItemsCount;

        return (
            <>
                <MetaTags
                    currentLang={langCode}
                    langs={langCodes}
                    path={`/news`}
                    metaTitle={metaTitle}
                    title={title}
                    description={metaDescription}
                    promoAppId={appleAppMarketId}
                    mediaItem={newsFeedMediaItems && newsFeedMediaItems[0]}
                    keywords={keywords}
                />
                <div className={container.grid}>
                    <h1 className={commonGridedTitle}>{title}</h1>
                    {description && (
                        <p className={commonDescription}>{description}</p>
                    )}
                    <FeedContainer>
                        {edges.map(
                            (
                                {
                                    node: {
                                        id,
                                        content: {
                                            title,
                                            readingTime,
                                            preview,
                                        },
                                        image,
                                        date,
                                    },
                                    cursor,
                                },
                                i,
                            ) => (
                                <Card
                                    key={cursor}
                                    title={title}
                                    date={date}
                                    mediaItem={image}
                                    srcSetSizes={
                                        i === 0
                                            ? {
                                                  desktop: 650,
                                                  laptop: 690,
                                                  tablet: 710,
                                              }
                                            : {
                                                  largePhone: 200,
                                              }
                                    }
                                    link={`/${langCode}/news/${id}`}
                                    description={preview}
                                    readingTime={readingTime}
                                    size={i === 0 ? "large" : "small"}
                                    onClickHandler={() => {
                                        gaFeedItemClick(
                                            `/news/${id}`,
                                            langCode,
                                            `/news`,
                                        );
                                    }}
                                />
                            ),
                        )}
                        {hasMoreNews && (
                            <LoadMoreButton
                                loading={loading}
                                handleLoadMore={handleLoadMore}
                            />
                        )}
                    </FeedContainer>
                    <AppBannersContainer>
                        <div className={appBannerContainer}>
                            <AppBanners
                                promoApps={promoApps}
                                location="newsFeed"
                                item="promoBanner"
                            />
                        </div>
                    </AppBannersContainer>
                </div>
            </>
        );
    }

    if (loading) {
        return <Spinner />;
    }

    return <NotFoundErrorMsg />;
};

export default NewsFeed;
